// pages/receipts/ReceiptList.jsx
// List-first view of receipts (money RECEIVED against an order). "Add New" opens the
// form modal; 🖨 on a row fetches that order and prints the receipt document.
import { useEffect, useMemo, useState } from "react";
import { ordersAPI, receiptsAPI } from "../../services/api";
import { usePagination } from "../../hooks/usePagination";
import { Loading, Pagination, EmptyState, Spinner } from "../../components/ui";
import { useToast } from "../../contexts/ToastContext";
import { fmt } from "../../utils/helpers";
import ReceiptFormModal from "./ReceiptFormModal";
import ReceiptModal from "../orders/ReceiptModal";

const MODE_BADGE = {
  Cash:   "badge-green",
  UPI:    "badge-blue",
  Card:   "badge-purple",
  Cheque: "badge-orange",
};

export default function ReceiptList() {
  const toast = useToast();

  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState("");
  const [search, setSearch]     = useState("");
  const [showForm, setShowForm] = useState(false);

  // Date range is filtered SERVER-side, so changing it refetches. Search stays client
  // side — it's instant and the endpoint has no name filter.
  const [from, setFrom] = useState("");
  const [to, setTo]     = useState("");

  // Printing: a receipt row only carries orderId, so the full order has to be fetched
  // before the document can render. printingId drives the per-row spinner.
  const [printingId, setPrintingId]   = useState(null);
  const [printTarget, setPrintTarget] = useState(null); // { order, receipt }

  const load = () => {
    setLoading(true);
    setError("");
    receiptsAPI
      .list({ fromDate: from, toDate: to })
      .then((d) => setReceipts(Array.isArray(d) ? d : []))
      .catch((e) => {
        console.error("[ReceiptList] load error:", e);
        setReceipts([]);
        setError(e?.message || "The receipts service isn't available right now.");
      })
      .finally(() => setLoading(false));
  };
  useEffect(load, [from, to]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return receipts;
    return receipts.filter(
      (r) =>
        (r.customerName ?? "").toLowerCase().includes(q) ||
        String(r.orderNo ?? "").toLowerCase().includes(q) ||
        String(r.orderId ?? "").toLowerCase().includes(q) ||
        String(r.id ?? "").toLowerCase().includes(q)
    );
  }, [receipts, search]);

  const { paged, page, setPage, total, count } = usePagination(filtered, 8);

  const totalReceived = receipts.reduce((s, r) => s + (r.amount || 0), 0);
  const totalKasar    = receipts.reduce((s, r) => s + (r.kasar  || 0), 0);

  const rangeLabel =
    from && to ? `${fmt(from)} → ${fmt(to)}`
    : from     ? `From ${fmt(from)}`
    : to       ? `Until ${fmt(to)}`
    : "All time";

  const handlePrint = async (r) => {
    if (!r.orderId) return toast("This receipt isn't linked to an order", "error");
    setPrintingId(r.id ?? r.orderId);
    try {
      const order = await ordersAPI.get(r.orderId);
      if (!order) throw new Error("Order not found");
      setPrintTarget({ order, receipt: r });
    } catch (e) {
      console.error("[ReceiptList] print error:", e);
      toast("Couldn't load that order", "error");
    } finally {
      setPrintingId(null);
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Receipts</h1>
          <p className="page-sub">
            {receipts.length} receipt{receipts.length !== 1 ? "s" : ""} · ₹{totalReceived.toLocaleString()} received
            {totalKasar > 0 ? ` · ₹${totalKasar.toLocaleString()} kasar` : ""} · {rangeLabel}
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowForm(true)}>
          ＋ Add New
        </button>
      </div>

      {/* Date range — server-side filter */}
      <div style={{ display: "flex", gap: 12, alignItems: "flex-end", marginBottom: 20, flexWrap: "wrap" }}>
        <div className="form-group" style={{ margin: 0 }}>
          <label className="form-label">From</label>
          <input type="date" className="form-input" value={from} max={to || undefined}
                 onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label className="form-label">To</label>
          <input type="date" className="form-input" value={to} min={from || undefined}
                 onChange={(e) => setTo(e.target.value)} />
        </div>
        {(from || to) && (
          <button className="btn btn-ghost btn-sm" onClick={() => { setFrom(""); setTo(""); }}>
            ✕ Clear dates
          </button>
        )}
      </div>

      <div className="card">
        <div className="card-header">
          <div className="search-bar" style={{ flex: 1, maxWidth: 340 }}>
            <span className="search-icon">🔍</span>
            <input
              className="form-input"
              placeholder="Search customer, order or receipt ID…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {search && <button className="btn btn-ghost btn-sm" onClick={() => setSearch("")}>✕ Clear</button>}
        </div>

        {loading ? (
          <Loading />
        ) : error ? (
          <EmptyState
            icon="⚠️"
            title="Couldn't load receipts"
            subtitle={error}
            action={<button className="btn btn-secondary btn-sm" onClick={load}>Retry</button>}
          />
        ) : (
          <>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Receipt ID</th>
                    <th>Order</th>
                    <th>Customer</th>
                    <th>Amount</th>
                    <th>Kasar</th>
                    <th>Mode</th>
                    <th>Date</th>
                    <th>Remarks</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paged.length === 0 ? (
                    <tr>
                      <td colSpan={9}>
                        <EmptyState
                          icon="🧾"
                          title={
                            search      ? "No receipts match your search"
                            : from || to ? "No receipts in this date range"
                            : "No receipts yet"
                          }
                          subtitle={search || from || to ? undefined : "Click “Add New” to record your first receipt."}
                        />
                      </td>
                    </tr>
                  ) : (
                    paged.map((r, i) => {
                      const busy = printingId === (r.id ?? r.orderId);
                      return (
                        <tr key={r.id ?? `${r.orderId}-${i}`}>
                          <td className="td-code">{r.id ?? "—"}</td>
                          {/* OM_No is what the shop calls the order; orderId is the fallback
                              when the order row was deleted (the join is LEFT). */}
                          <td className="td-muted">{r.orderNo ? `#${r.orderNo}` : r.orderId ? `#${r.orderId}` : "—"}</td>
                          <td><strong>{r.customerName || "—"}</strong></td>
                          <td style={{ color: "var(--gold)", fontWeight: 600 }}>₹{(r.amount || 0).toLocaleString()}</td>
                          <td className="td-muted">{r.kasar ? `₹${r.kasar.toLocaleString()}` : "—"}</td>
                          <td>
                            <span className={`badge ${MODE_BADGE[r.mode] || "badge-blue"}`}>
                              <span className="badge-dot" />{r.mode || "—"}
                            </span>
                          </td>
                          <td className="td-muted">{fmt(r.date)}</td>
                          <td className="td-muted">{r.remarks || "—"}</td>
                          <td>
                            <button
                              className="btn btn-ghost btn-sm"
                              title={r.orderId ? "Generate receipt" : "No order linked"}
                              disabled={busy || !r.orderId}
                              style={!r.orderId ? { opacity: 0.4, cursor: "not-allowed" } : undefined}
                              onClick={() => handlePrint(r)}
                            >
                              {busy ? <Spinner /> : "🖨"}
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <Pagination page={page} total={total} setPage={setPage} count={count} perPage={8} />
          </>
        )}
      </div>

      {showForm && (
        <ReceiptFormModal
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); load(); }}
        />
      )}

      {printTarget && (
        <ReceiptModal
          order={printTarget.order}
          receipt={printTarget.receipt}
          onClose={() => setPrintTarget(null)}
        />
      )}
    </div>
  );
}
